/**
 * Credimport Global Pricing — custom-pricing.js
 *
 * 1. Fetches BCV rate from the public API
 * 2. Persists selected currency (USD/VES) in localStorage
 * 3. Listens for clicks on .ci-flag-option (global switcher) AND .flag-option (product page)
 * 4. Dispatches 'currency:changed' event for other scripts to react
 * 5. Updates all price containers site-wide
 * 6. Injects hidden _moneda_pago property into all add-to-cart forms
 */
(function () {
  'use strict';

  /* ─── Constants ───────────────────────────────────────────────── */
  var STORAGE_KEY = 'credimport_moneda';
  var RATE_CACHE_KEY = 'credimport_bcv_cache';
  var RATE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  var BCV_API_URL = 'https://intuitivotech.com/credimport/api/public-bcv-rate.php';
  var MARKUP_STRIKETHROUGH_COLOR = '#e52e06';

  /* ─── State ───────────────────────────────────────────────────── */
  var state = {
    currency: 'USD',
    bcvRate: null,
    fetching: false
  };

  /* ─── Helpers ─────────────────────────────────────────────────── */

  function getStoredCurrency() {
    try { return localStorage.getItem(STORAGE_KEY) || 'USD'; } catch (e) { return 'USD'; }
  }

  function storeCurrency(c) {
    try { localStorage.setItem(STORAGE_KEY, c); } catch (e) { /* noop */ }
  }

  /**
   * Extract the markup percentage from a collection handle.
   * Supports: markup-35, markup-30, markup-10, markup-5, etc.
   * Returns the number (e.g. 35) or 0 if no match.
   */
  function extractMarkupFromHandle(handle) {
    if (!handle) return 0;
    var m = handle.match(/markup[- ]?(\d+)/i);
    return m ? parseInt(m[1], 10) : 0;
  }

  /**
   * Given a data-markup attribute value (from Liquid), return the discount multiplier.
   * data-markup="35" → the Shopify price is inflated 35%, so real price = base * (100-35)/100 = base * 0.65
   */
  function discountMultiplier(markupPct) {
    if (!markupPct || markupPct <= 0) return 1;
    return (100 - markupPct) / 100;
  }

  function formatUSD(amount) {
    return '$' + amount.toFixed(2);
  }

  function formatVES(amount) {
    return 'Bs. ' + amount.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* ─── BCV Rate ────────────────────────────────────────────────── */

  function getCachedRate() {
    try {
      var raw = sessionStorage.getItem(RATE_CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (Date.now() - cached.ts < RATE_CACHE_TTL && cached.rate > 0) {
        return cached.rate;
      }
    } catch (e) { /* noop */ }
    return null;
  }

  function setCachedRate(rate) {
    try {
      sessionStorage.setItem(RATE_CACHE_KEY, JSON.stringify({ rate: rate, ts: Date.now() }));
    } catch (e) { /* noop */ }
  }

  function fetchBcvRate(callback) {
    var cached = getCachedRate();
    if (cached) {
      state.bcvRate = cached;
      try {
        document.dispatchEvent(new CustomEvent('credimport:bcv-rate', { detail: { rate: cached } }));
      } catch (e) { /* noop */ }
      if (callback) callback(cached);
      return;
    }
    if (state.fetching) return;
    state.fetching = true;
    fetch(BCV_API_URL)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.success && data.rate) {
          state.bcvRate = parseFloat(data.rate);
          setCachedRate(state.bcvRate);
        }
        state.fetching = false;
        if (callback) callback(state.bcvRate);
      })
      .catch(function () {
        state.fetching = false;
        if (callback) callback(state.bcvRate);
        try {
          document.dispatchEvent(new CustomEvent('credimport:bcv-rate', { detail: { rate: state.bcvRate } }));
        } catch (e) { /* noop */ }
      });
  }

  /* ─── Price Update Engine ─────────────────────────────────────── */

  /**
   * Find all price containers and update them.
   * Each container should have:
   *   data-base-price="<price in USD as float>"
   *   data-markup="<markup percentage>" (optional, e.g. "35")
   *   data-quantity="<qty>" (optional, for cart items)
   */
  function updateAllPrices() {
    var containers = document.querySelectorAll('[data-base-price]');
    for (var i = 0; i < containers.length; i++) {
      updatePriceContainer(containers[i]);
    }
    // Update cart totals if any
    updateCartTotals();
  }

  function updatePriceContainer(el) {
    var basePrice = parseFloat(el.getAttribute('data-base-price'));
    if (!basePrice || isNaN(basePrice)) return;

    var markupPct = parseInt(el.getAttribute('data-markup') || '0', 10);
    var qty = parseInt(el.getAttribute('data-quantity') || '1', 10);
    var isProductDetail = el.classList.contains('tp-product-details-price-wrapper');

    // Locate or create price display elements
    var priceEl = el.querySelector('.ci-price-display');
    var compareEl = el.querySelector('.ci-price-compare');

    if (!priceEl) {
      // First run: wrap existing content and create our elements
      setupPriceContainer(el, basePrice, markupPct);
      priceEl = el.querySelector('.ci-price-display');
      compareEl = el.querySelector('.ci-price-compare');
      if (!priceEl) return;
    }

    if (state.currency === 'USD') {
      if (markupPct > 0) {
        // Show inflated price struck through in red, real price in black
        var realPrice = basePrice * discountMultiplier(markupPct);
        if (compareEl) {
          compareEl.textContent = formatUSD(basePrice * qty);
          compareEl.style.display = 'inline';
        }
        priceEl.textContent = formatUSD(realPrice * qty);
        priceEl.style.color = '#000';
      } else {
        // Normal USD price
        if (compareEl) compareEl.style.display = 'none';
        priceEl.textContent = formatUSD(basePrice * qty);
        priceEl.style.color = '';
      }
    } else {
      // VES mode
      if (state.bcvRate && state.bcvRate > 0) {
        var vesPrice = basePrice * state.bcvRate;
        if (compareEl) compareEl.style.display = 'none';
        priceEl.textContent = formatVES(vesPrice * qty);
        priceEl.style.color = '#000';
      } else {
        // No rate yet, show loading
        if (compareEl) compareEl.style.display = 'none';
        priceEl.textContent = 'Cargando...';
      }
    }
  }

  function setupPriceContainer(el, basePrice, markupPct) {
    // Store original HTML for potential restoration
    if (!el.getAttribute('data-original-html')) {
      el.setAttribute('data-original-html', el.innerHTML);
    }

    // Don't setup if it's the product detail page handler from main-product.liquid
    // (that one has its own logic via #price-main-custom)
    var mainCustom = el.querySelector('#price-main-custom');
    if (mainCustom) return; // Let main-product.liquid handle its own

    // Create compare (struck-through) price
    var compareEl = document.createElement('span');
    compareEl.className = 'ci-price-compare';
    compareEl.style.cssText = 'text-decoration: line-through; color: ' + MARKUP_STRIKETHROUGH_COLOR + '; font-size: inherit; font-weight: bold; margin-right: 8px; display: none;';

    // Create main price display
    var priceEl = document.createElement('span');
    priceEl.className = 'ci-price-display';
    priceEl.style.cssText = 'font-weight: bold; font-size: inherit;';

    // Clear container and insert our elements
    el.innerHTML = '';
    el.appendChild(compareEl);
    el.appendChild(priceEl);
  }

  /* ─── Cart Totals ─────────────────────────────────────────────── */

  function updateCartTotals() {
    // Mini cart subtotal
    var miniCartSubtotal = document.querySelector('.cartmini__checkout-title span');
    if (miniCartSubtotal) {
      var miniItems = document.querySelectorAll('.cartmini__price-wrapper[data-base-price]');
      var total = 0;
      for (var i = 0; i < miniItems.length; i++) {
        var p = parseFloat(miniItems[i].getAttribute('data-base-price'));
        var mk = parseInt(miniItems[i].getAttribute('data-markup') || '0', 10);
        var q = parseInt(miniItems[i].getAttribute('data-quantity') || '1', 10);
        if (p) {
          if (state.currency === 'VES' && state.bcvRate > 0) {
            total += p * q * state.bcvRate;
          } else if (state.currency === 'USD') {
            total += p * q * discountMultiplier(mk);
          }
        }
      }
      if (total > 0) {
        miniCartSubtotal.textContent = state.currency === 'VES' ? formatVES(total) : formatUSD(total);
      }
    }

    // Cart page subtotal
    var cartSubtotal = document.querySelector('.tp-cart-checkout-top-price');
    if (cartSubtotal) {
      var cartItems = document.querySelectorAll('.tp-cart-price[data-base-price]');
      var cartTotal = 0;
      for (var j = 0; j < cartItems.length; j++) {
        var cp = parseFloat(cartItems[j].getAttribute('data-base-price'));
        var cmk = parseInt(cartItems[j].parentNode.querySelector('.tp-product-price-wrapper, .tp-cart-price')?.getAttribute('data-markup') || cartItems[j].getAttribute('data-markup') || '0', 10);
        var cq = parseInt(cartItems[j].getAttribute('data-quantity') || '1', 10);
        if (cp) {
          if (state.currency === 'VES' && state.bcvRate > 0) {
            cartTotal += cp * cq * state.bcvRate;
          } else if (state.currency === 'USD') {
            cartTotal += cp * cq * discountMultiplier(cmk);
          }
        }
      }
      if (cartTotal > 0) {
        if (!cartSubtotal.getAttribute('data-original')) {
          cartSubtotal.setAttribute('data-original', cartSubtotal.textContent.trim());
        }
        cartSubtotal.textContent = state.currency === 'VES' ? formatVES(cartTotal) : formatUSD(cartTotal);
      }
    }
  }

  /* ─── Switcher UI ─────────────────────────────────────────────── */

  function updateSwitcherUI(currency) {
    // Update all switchers on page (global + product page local)
    var allFlags = document.querySelectorAll('.ci-flag-option, .flag-option');
    for (var i = 0; i < allFlags.length; i++) {
      var flag = allFlags[i];
      if (flag.getAttribute('data-currency') === currency) {
        flag.classList.add('active');
        flag.style.opacity = '1';
      } else {
        flag.classList.remove('active');
        flag.style.opacity = '0.55';
      }
    }
  }

  function switchCurrency(newCurrency) {
    state.currency = newCurrency;
    storeCurrency(newCurrency);
    updateSwitcherUI(newCurrency);

    if (newCurrency === 'VES' && !state.bcvRate) {
      fetchBcvRate(function () {
        updateAllPrices();
        dispatchCurrencyEvent();
      });
    } else {
      updateAllPrices();
      dispatchCurrencyEvent();
    }
  }

  function dispatchCurrencyEvent() {
    try {
      window.dispatchEvent(new CustomEvent('currency:changed', {
        detail: {
          currency: state.currency,
          rate: state.bcvRate
        }
      }));
    } catch (e) { /* IE fallback */ }
  }

  /* ─── _moneda_pago injection ──────────────────────────────────── */

  function injectMonedaPago() {
    var forms = document.querySelectorAll('form[data-type="add-to-cart-form"], form.add-to-cart-rendom');
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var existing = form.querySelector('input[name="properties[_moneda_pago]"]');
      if (existing) {
        existing.value = state.currency;
      } else {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[_moneda_pago]';
        input.value = state.currency;
        form.appendChild(input);
      }
    }
  }

  /** Refresh _moneda_pago values whenever currency changes */
  function onCurrencyChangeUpdateForms() {
    injectMonedaPago();
  }

  /* ─── Initialization ──────────────────────────────────────────── */

  function init() {
    // Restore stored currency
    state.currency = getStoredCurrency();

    // Pre-fetch BCV rate
    fetchBcvRate(function () {
      updateAllPrices();
    });

    // Update switcher UI to match stored currency
    updateSwitcherUI(state.currency);

    // If user had VES, do initial conversion
    if (state.currency === 'VES') {
      updateAllPrices();
    }

    // Inject _moneda_pago into all forms
    injectMonedaPago();

    // Listen to switcher clicks — global switcher
    document.addEventListener('click', function (e) {
      var flag = e.target.closest('.ci-flag-option');
      if (flag) {
        var currency = flag.getAttribute('data-currency');
        if (currency) switchCurrency(currency);
        return;
      }
      // Also listen to the product page local switcher (.flag-option)
      var localFlag = e.target.closest('.flag-option');
      if (localFlag) {
        var cur = localFlag.getAttribute('data-currency');
        if (cur) switchCurrency(cur);
      }
    });

    // Listen for currency:changed to keep forms in sync
    window.addEventListener('currency:changed', onCurrencyChangeUpdateForms);

    // Observe DOM mutations for dynamically loaded content (quickview, AJAX cart, etc.)
    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function (mutations) {
        var shouldUpdate = false;
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes.length > 0) {
            shouldUpdate = true;
            break;
          }
        }
        if (shouldUpdate) {
          // Delay slightly to let DOM settle
          setTimeout(function () {
            injectMonedaPago();
            updateAllPrices();
          }, 100);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  // Run on DOMContentLoaded or immediately if already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for other scripts
  window.CredimportPricing = {
    getRate: function () { return state.bcvRate; },
    getCurrency: function () { return state.currency; },
    switchCurrency: switchCurrency,
    refreshPrices: updateAllPrices
  };

})();
